import React, { useEffect, useState } from "react";
import {
  RefreshCw,
  Loader2,
  TrendingUp,
  TrendingDown,
  Package,
  RotateCcw,
  IndianRupee,
  AlertCircle,
  CheckCircle2,
  Calendar,
  Plug,
} from "lucide-react";
import { Link } from "react-router-dom";
import { AppShell } from "../components/AppShell";
import { api, errMsg } from "../lib/api";
import { toast } from "sonner";

/**
 * Daily Sales Sync — Feature (b).
 *
 * Lets the seller pull yesterday's headline KPIs (gross sales, orders,
 * RTO, ad spend, delivered) from Myntra Partner Portal in one click.
 * Headless Chromium logs in, scrapes, returns; data is persisted in
 * `myntra_daily_sales` so each day's row builds a trend.
 *
 * If Myntra credentials are missing, the page nudges the seller to
 * Connect Myntra first.
 */

const KPI_DEFS = [
  { key: "gross_sales", label: "Gross Sales", icon: IndianRupee, prefix: "₹", color: "#FF3F6C" },
  { key: "orders", label: "Total Orders", icon: Package, color: "#FF905A" },
  { key: "delivered", label: "Delivered", icon: CheckCircle2, color: "#03A685" },
  { key: "rto_count", label: "Returns / RTO", icon: RotateCcw, color: "#FB8800" },
  { key: "ad_spend", label: "Ad Spend", icon: TrendingUp, prefix: "₹", color: "#535665" },
];

function formatNumber(v, prefix = "") {
  if (v === null || v === undefined || Number.isNaN(v)) return "—";
  const num = Number(v);
  if (num >= 100000) return `${prefix}${(num / 100000).toFixed(2)}L`;
  if (num >= 1000) return `${prefix}${(num / 1000).toFixed(1)}K`;
  return `${prefix}${num.toFixed(0)}`;
}

function dayDelta(today, yesterday) {
  if (!today || !yesterday || yesterday === 0) return null;
  return ((today - yesterday) / yesterday) * 100;
}

export default function MyntraSync() {
  const [creds, setCreds] = useState(null);
  const [rows, setRows] = useState([]);
  const [syncing, setSyncing] = useState(false);
  const [loading, setLoading] = useState(true);

  async function refresh() {
    setLoading(true);
    try {
      const [statusRes, rowsRes] = await Promise.all([
        api.get("/seller/integrations/myntra/status"),
        api.get("/seller/integrations/myntra/sales/recent?days=14"),
      ]);
      setCreds(statusRes.data.credentials);
      setRows(rowsRes.data.rows || []);
    } catch (e) {
      // 401 handled by interceptor
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    refresh();
  }, []);

  async function handleSync() {
    setSyncing(true);
    try {
      const { data } = await api.post("/seller/integrations/myntra/sync-sales");
      if (data.ok) {
        toast.success(`Synced ${data.date} — ${Object.keys(data.metrics || {}).length} metric(s)`);
      } else {
        toast.error(data.message || "Sync failed");
      }
      await refresh();
    } catch (err) {
      toast.error(errMsg(err, "Sync failed"));
    } finally {
      setSyncing(false);
    }
  }

  const latest = rows[0];
  const prior = rows[1];

  // Empty state — no credentials saved
  if (!loading && !creds) {
    return (
      <AppShell role="seller">
        <div className="max-w-3xl mx-auto py-12">
          <div
            className="rounded-2xl p-8 text-center"
            style={{
              background: "#FFFFFF",
              border: "1px solid #EDEDF1",
              boxShadow: "0 4px 24px -12px rgba(15,23,42,0.08)",
            }}
            data-testid="sync-no-creds"
          >
            <div
              className="w-14 h-14 rounded-2xl flex items-center justify-center mx-auto mb-4"
              style={{
                background: "linear-gradient(135deg, #FF3F6C 0%, #FF905A 100%)",
              }}
            >
              <Plug className="text-white" size={24} />
            </div>
            <h1 className="text-2xl font-bold mb-2" style={{ color: "#282C3F" }}>
              Connect Myntra first
            </h1>
            <p className="text-sm mb-6" style={{ color: "#696B79" }}>
              Daily Sales Sync needs your Myntra Partner Portal credentials.
              Save them once — encrypted — and pull yesterday's numbers in one click.
            </p>
            <Link
              to="/seller/integrations/myntra"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold text-white shadow-md hover:shadow-lg transition-all"
              style={{
                background: "linear-gradient(135deg, #FF3F6C 0%, #FF905A 100%)",
              }}
              data-testid="sync-connect-cta"
            >
              <Plug size={14} />
              Connect Myntra
            </Link>
          </div>
        </div>
      </AppShell>
    );
  }

  return (
    <AppShell role="seller">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-3xl font-bold tracking-tight" style={{ color: "#282C3F" }}>
              Daily Sales Sync
            </h1>
            <p className="text-sm mt-1" style={{ color: "#696B79" }}>
              One-click pull of yesterday's headline numbers from Myntra Partner Portal.
            </p>
          </div>
          <button
            onClick={handleSync}
            disabled={syncing || !creds}
            className="px-5 py-2.5 rounded-xl text-sm font-semibold text-white shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed transition-all inline-flex items-center gap-2"
            style={{
              background: "linear-gradient(135deg, #FF3F6C 0%, #FF905A 100%)",
            }}
            data-testid="sync-now-btn"
          >
            {syncing ? (
              <>
                <Loader2 className="animate-spin" size={14} />
                Syncing… (~30s)
              </>
            ) : (
              <>
                <RefreshCw size={14} />
                Sync now
              </>
            )}
          </button>
        </div>

        {/* Connection status hint */}
        {creds && creds.last_login_at && (
          <div
            className="mt-4 text-xs inline-flex items-center gap-2 px-3 py-1.5 rounded-full"
            style={{ background: "#F1FAF6", color: "#03A685", border: "1px solid #C9EBD9" }}
            data-testid="sync-conn-status"
          >
            <CheckCircle2 size={12} />
            Myntra connected as <strong>{creds.email_masked}</strong>
          </div>
        )}

        {/* Latest day KPIs */}
        {latest ? (
          <>
            <div className="mt-8 flex items-center gap-2 text-xs font-semibold uppercase tracking-wider" style={{ color: "#7E818C" }}>
              <Calendar size={12} />
              Latest sync — {latest.date}
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 mt-3">
              {KPI_DEFS.map(({ key, label, icon: Icon, prefix, color }) => {
                const val = latest.metrics?.[key];
                const prev = prior?.metrics?.[key];
                const delta = dayDelta(val, prev);
                const positive = key === "rto_count" ? delta < 0 : delta > 0;
                return (
                  <div
                    key={key}
                    className="rounded-2xl p-4"
                    style={{
                      background: "#FFFFFF",
                      border: "1px solid #EDEDF1",
                      boxShadow: "0 1px 4px -1px rgba(15,23,42,0.04)",
                    }}
                    data-testid={`sync-kpi-${key}`}
                  >
                    <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wider" style={{ color: "#7E818C" }}>
                      <Icon size={11} style={{ color }} />
                      {label}
                    </div>
                    <div className="text-2xl font-bold mt-2" style={{ color: "#282C3F" }}>
                      {formatNumber(val, prefix)}
                    </div>
                    {delta !== null && (
                      <div
                        className="text-xs mt-1 inline-flex items-center gap-1"
                        style={{ color: positive ? "#03A685" : "#E94560" }}
                      >
                        {positive ? <TrendingUp size={10} /> : <TrendingDown size={10} />}
                        {Math.abs(delta).toFixed(1)}% vs prior day
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </>
        ) : (
          !loading && (
            <div
              className="mt-8 rounded-2xl p-8 text-center"
              style={{
                background: "#FFFFFF",
                border: "1px dashed #E9E9ED",
                color: "#7E818C",
              }}
              data-testid="sync-no-data"
            >
              <AlertCircle size={20} className="mx-auto mb-2" style={{ color: "#FF905A" }} />
              No sales synced yet. Click <strong style={{ color: "#FF3F6C" }}>Sync now</strong> to pull yesterday's numbers.
            </div>
          )
        )}

        {/* 14-day history table */}
        {rows.length > 0 && (
          <div className="mt-10">
            <h2 className="text-sm font-semibold uppercase tracking-wider mb-3" style={{ color: "#7E818C" }}>
              Last 14 days
            </h2>
            <div
              className="rounded-2xl overflow-hidden"
              style={{
                background: "#FFFFFF",
                border: "1px solid #EDEDF1",
              }}
            >
              <table className="w-full text-sm" data-testid="sync-history-table">
                <thead>
                  <tr style={{ background: "#FAFAFB", borderBottom: "1px solid #EDEDF1" }}>
                    <th className="text-left py-2.5 px-4 text-xs font-semibold uppercase tracking-wider" style={{ color: "#7E818C" }}>
                      Date
                    </th>
                    {KPI_DEFS.map(({ key, label, prefix }) => (
                      <th
                        key={key}
                        className="text-right py-2.5 px-4 text-xs font-semibold uppercase tracking-wider"
                        style={{ color: "#7E818C" }}
                      >
                        {label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.date} style={{ borderBottom: "1px solid #F4F4F6" }}>
                      <td className="py-2.5 px-4 font-medium" style={{ color: "#282C3F" }}>
                        {r.date}
                      </td>
                      {KPI_DEFS.map(({ key, prefix }) => (
                        <td
                          key={key}
                          className="text-right py-2.5 px-4 font-mono-data"
                          style={{ color: "#535665" }}
                        >
                          {formatNumber(r.metrics?.[key], prefix)}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {loading && (
          <div className="mt-6 text-sm inline-flex items-center gap-2" style={{ color: "#7E818C" }}>
            <Loader2 className="animate-spin" size={14} />
            Loading…
          </div>
        )}
      </div>
    </AppShell>
  );
}
